// Accessibility hook — true when the OS / browser signals
// `prefers-reduced-motion: reduce`. Components should branch on this to
// suppress entrance animations, shake, parallax, or any motion that
// could trigger vestibular discomfort.
//
// SSR-safe: returns `false` when window is undefined.

import { useEffect, useState } from 'react';
import { useAccountStore } from '../stores/account';

const QUERY = '(prefers-reduced-motion: reduce)';

/** Raw OS preference. Use `useEffectiveReducedMotion` to combine with user override. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(QUERY);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    // matchMedia in Safari < 14 used addListener/removeListener — we only
    // need modern browsers for v2, so addEventListener is fine.
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

/**
 * Effective reduced-motion preference, combining the OS signal with the
 * user's explicit override from Settings.
 *
 *   'auto' → follow the OS (`useReducedMotion()`)
 *   'on'   → force reduced motion regardless of OS
 *   'off'  → force full motion regardless of OS (the player explicitly
 *            opted back in, e.g., they want the FIGHT intro despite the
 *            OS still asking for reduced motion)
 *
 * Components consuming motion preferences should default to this hook
 * unless they specifically need the raw OS value.
 */
export function useEffectiveReducedMotion(): boolean {
  const osReduced = useReducedMotion();
  const pref = useAccountStore((s) => s.reducedMotionPref);
  if (pref === 'on') return true;
  if (pref === 'off') return false;
  return osReduced;
}

// Layout branching lives in useLayoutMode.ts — this file keeps only the
// motion-preference hooks.
